// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { DialogTitle } from '@mui/material';
import { IconButton, type TriggerProps } from '@shared/ui-kit';
import { classNames } from '@shared/ui-kit/utils';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import silverAlphaLogo from '@shared/ui-kit/assets/img/silver-alpha.png';
import { useModal } from './use-modal';
import { ScreenRenderer } from '../screen-renderer';
import { ProgressTracker } from '../progress-tracker';

interface ModalProps {
  Trigger: FC<TriggerProps>;
  modalTestId?: string;
  showHeaderLogo?: boolean;
}

export const Modal: FC<ModalProps> = ({
  Trigger,
  modalTestId,
  showHeaderLogo = true,
}) => {
  const { t } = useTranslation();
  const {
    Modal: ModalRenderer,
    modal,
    hideProgressTracker,
    isDisabled,
    isHeaderLogoVisible,
  } = useModal(showHeaderLogo);

  return (
    <ModalRenderer
      Trigger={Trigger}
      PaperProps={{
        className: classNames(
          '!max-w-[95vw] !max-h-[90vh] !h-auto !w-full !flex !flex-col !overflow-hidden',
          hideProgressTracker ? 'lg:!max-w-[640px]' : 'lg:!max-w-[960px]',
        ),
      }}
      data-testid={modalTestId ?? 'account-workflow-modal'}
      triggerDisabled={isDisabled}
    >
      <>
        <DialogTitle
          component="div"
          className="!absolute !right-0 !top-0 !z-10 !bg-transparent"
          data-testid="onboarding-modal-title"
        >
          <IconButton
            icon="Close"
            onClick={modal.onClose}
            data-testid="close-button"
            variant="icon"
            color="disabled"
            size="md"
          />
        </DialogTitle>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {!hideProgressTracker && <ProgressTracker />}

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {isHeaderLogoVisible && (
              <div
                className="flex shrink-0 items-center px-6 pb-2 pr-14 pt-4"
                data-testid="onboarding-modal-logo-wrapper"
              >
                <img
                  alt={t('glossary.silver-alpha-asset-management-sgiic', {
                    ns: 'glossary',
                  })}
                  className="h-11 w-auto max-w-[min(100%,16rem)] object-contain object-left md:h-12"
                  data-testid="onboarding-modal-logo"
                  src={silverAlphaLogo}
                />
              </div>
            )}

            <ScreenRenderer hasHeaderLogo={isHeaderLogoVisible} />
          </div>
        </div>
      </>
    </ModalRenderer>
  );
};
